const normalizeHex6 = (hex: string): string => {
  const normalized = hex.startsWith('#') ? hex : `#${hex}`
  const h = normalized.slice(1, 7)
  if (h.length !== 6 || !/^[0-9a-fA-F]{6}$/.test(h)) {
    throw new Error(`expected #RRGGBB, got ${hex}`)
  }
  return `#${h.toLowerCase()}`
}

/**
 * 6-char `#RRGGBB` → `#RRGGBBAA` for SVG strokes etc. Alpha is `0`–`1`.
 */
export const hexWithAlpha = (hex: string, alpha: number): string => {
  const h = normalizeHex6(hex).slice(1)
  const a = Math.round(Math.min(1, Math.max(0, alpha)) * 255)
  return `#${h}${a.toString(16).padStart(2, '0')}`
}

const hexChannel = (hex6: string, offset: number): number =>
  parseInt(hex6.slice(offset, offset + 2), 16)

/** Opaque mix of two `#RRGGBB` colors. `amount` is the weight of `to` (`0`–`1`). */
export const hexMix = (from: string, to: string, amount: number): string => {
  const a = normalizeHex6(from).slice(1)
  const b = normalizeHex6(to).slice(1)
  const t = Math.min(1, Math.max(0, amount))
  const mix = (offset: number) =>
    Math.round(hexChannel(a, offset) * (1 - t) + hexChannel(b, offset) * t)
      .toString(16)
      .padStart(2, '0')
  return `#${mix(0)}${mix(2)}${mix(4)}`
}

const presets = {
  blue: {
    5: '#F4F5F8',
    10: '#E6F0FE',
    100: '#203F59',
    20: '#C8E0FD',
    30: '#A1CDFB',
    40: '#6DB8F9',
    /** Figma PTP grouped bar median transit — bright sky (blue.50), not muted blue.45. */
    45: '#7EB8E0',
    50: '#26A7F7',
    /** Figma PTP pickups “before” steel blue (between 50 and 60). */
    55: '#4D78BC',
    60: '#298CCE',
    70: '#2978B0',
    80: '#286693',
    90: '#26567B',
  },
  green: {
    10: '#EBF1EC',
    100: '#284130',
    20: '#D5E1D8',
    30: '#B9CDBE',
    40: '#9DB8A4',
    50: '#80A289',
    /** Figma PTP pickups “after” / carrier mint (logo green). */
    55: '#81C99B',
    60: '#648E71',
    70: '#427653',
    80: '#3C6A4B',
    90: '#33583F',
  },
  neutral: {
    0: '#FFFFFF',
    10: '#F3F4F4',
    100: '#121212',
    25: '#EAEAEA',
    20: '#DEDEDE',
    30: '#C8C8C8',
    40: '#B1B1B1',
    50: '#9A9A9A',
    60: '#858585',
    70: '#737373',
    80: '#616161',
    90: '#515151',
  },
  pink: {
    10: '#FDEBEE',
    100: '#6E1E39',
    20: '#FAD4DC',
    30: '#F4B9C6',
    40: '#ED9AAE',
    50: '#E37A96',
    60: '#D95A81',
    70: '#CA2265',
    80: '#B7225C',
    90: '#98224D',
  },
  red: {
    10: '#FFEAE2',
    100: '#602E20',
    20: '#FFD4C5',
    30: '#FFB79F',
    40: '#FF9575',
    50: '#FF6C47',
    55: '#B71C1C',
    60: '#DC5E3F',
    70: '#BB5237',
    80: '#9E4730',
    90: '#843C29',
  },
  turquoise: {
    10: '#ECFBF8',
    95: '#0F3744',
    100: '#062321',
    20: '#A4EBDF',
    30: '#44DAC8',
    40: '#40C4B4',
    50: '#3BAB9D',
    55: '#22AC9D',
    60: '#369388',
    70: '#317F74',
    80: '#2C6B63',
    90: '#275953',
  },
  /** Figma "shade" scale — navy text/icons on light surfaces. */
  shade: {
    700: '#2E3A59',
  },
  yellow: {
    5: '#FFFBF0',
    10: '#FFEEC5',
    100: '#483B14',
    20: '#FBDB82',
    30: '#ECC30B',
    40: '#D2AE12',
    50: '#B79816',
    60: '#9F8318',
    70: '#887018',
    80: '#745F18',
    90: '#615017',
  },
  /** Dark mode surface backgrounds (filter editor, selects, dropdowns) */
  dark: {
    surface: '#232526', // select/picklist/date input bg, dropdown bg
    surfacePanel: '#181A1B', // filter editor popover panel
  },
  /** Overlay colors for surfaces on dark/light backgrounds */
  overlay: {
    white02: '#FFFFFF05',
    white13: '#FFFFFF21',
    black03: '#00000008',
    black05: '#0000000D',
    black10: '#0000001A',
    turquoise50: '#40C4B480',
    turquoise40: '#40C4B466', // rgba(64, 196, 180, 0.40) - border on dark filter editor
    turquoise30: '#40C4B44D',
    turquoise90_40: '#27595366', // rgba(39, 89, 83, 0.40) - filter button active
    neutral10_10: '#F3F4F41A', // rgba(243, 244, 244, 0.10) - dropdown option hover
    /** presets.turquoise.100 @ 95% — legacy translucent overlay */
    turquoise100_95: '#062321F2',
  },
  /** Temporary palette values pending final design-token placement. */
  temporary: {
    // TODO: Replace with finalized design token once design-system placement is decided.
    infoCardDarkBorder: '#206463',
  },
}

/**
 * Opaque marker fill for a series stroke: same family, step 10
 * (turquoise.50 → turquoise.10, blue.80 → blue.10, red.50 → red.10).
 * Translucent fills look muddy on dark chart cards.
 */
export const chartMarkerFill = (strokeHex: string): string => {
  const n = normalizeHex6(strokeHex)
  for (const family of Object.values(presets)) {
    if (family == null || typeof family !== 'object' || !('10' in family)) {
      continue
    }
    const steps = family as Record<string, string>
    const hit = Object.values(steps).some(
      value =>
        typeof value === 'string' &&
        value.length === 7 &&
        value.toLowerCase() === n,
    )
    if (hit) return steps['10']
  }
  return hexMix('#ffffff', n, 0.12)
}

/**
 * Port-to-Port Transit Dashboard (Figma 3649-6112) carrier donut slice order on light cards.
 * Dark teal → mint → orange → yellow → navy → steel blue → sky → teal → light blue → unlading teal.
 */
const portToPortPiePalette = [
  presets.turquoise['80'],
  presets.green['55'],
  presets.red['50'],
  presets.yellow['30'],
  presets.blue['90'],
  presets.blue['55'],
  presets.blue['50'],
  presets.turquoise['70'],
  presets.blue['30'],
  presets.turquoise['50'],
] as const

/** Semantic series fills for Port-to-Port Transit charts (Figma 3649-6112). */
const portToPortChartSeries = {
  groupedBarAvg: presets.blue['90'],
  groupedBarMedian: presets.blue['50'],
  momAvg: presets.red['50'],
  momMedian: presets.pink['70'],
  horizontalDwell: presets.turquoise['50'],
  /** Figma 3649-6112 pickups stacked bar: maroon before + salmon after. */
  pickupsBefore: presets.pink['100'],
  pickupsAfter: presets.red['30'],
  pie: [...portToPortPiePalette],
} as const

/** Home dashboard snapshot chart series (Figma Home — Dashboard, node 3711-4539). */
const homeDashboardChartSeries = {
  predictedLateWeekly: presets.blue['90'],
  predictedLateByCarrier: presets.turquoise['50'],
  containersAtTransshipment: presets.blue['50'],
  outForDelivery: presets.pink['80'],
} as const

/** Home dashboard UI accents (Figma Home — Dashboard, node 3711-4539). */
const homeDashboardAccents = {
  accentTeal: presets.turquoise['60'],
  mintPanel: presets.green['10'],
  iconOnFilledTeal: presets.neutral['0'],
  /** Gray entity icons left of search placeholder text (Figma 3711-4539). */
  inputRouteIcon: presets.neutral['60'],
} as const

export const colors = {
  body: {
    bg: '#FFFFFF',
  },
  topbar: {
    bg: '#EAEAEA',
  },
  sidebar: {
    bg: '#061C23',
  },
  goalColors: {
    goodBlue: presets.blue['50'],
    goodTurquoise: presets.turquoise['50'],
    badPink: presets.pink['70'],
    neutralGray: presets.neutral['60'],
    _dark: {
      goodTurquoise: presets.turquoise['40'],
      badRed: presets.red['50'],
    },
  },
  checkbox: {
    bg: '#3BAB9D',
    500: '#3BAB9D',
  },
  stackedChart: {
    default: '#333333',
  },
  chart: {
    barFill: presets.turquoise['50'],
    seriesPrimary: presets.blue['50'],
    seriesSecondary: presets.blue['20'],
    /** Cartesian grid dashes: `presets.neutral.30` (Figma 4640:2892). */
    gridStroke: presets.neutral['30'],
    /** Solid value-0 grid line: full `presets.neutral.20`. */
    gridStrokeZero: presets.neutral['20'],
    /** Inactive bar segment fill: white @ 8%. */
    barBackground: hexWithAlpha(presets.neutral['0'], 0.08),
    /** Default multi-series line / bar palette (order preserved for cycling). */
    palette: [
      presets.turquoise['30'],
      presets.blue['80'],
      presets.blue['50'],
      presets.turquoise['70'],
      presets.pink['70'],
      presets.turquoise['50'],
      presets.blue['90'],
      presets.turquoise['40'],
      presets.blue['40'],
      presets.turquoise['90'],
      presets.blue['60'],
      presets.turquoise['80'],
    ],
    /**
     * Pie / donut — Port-to-Port Transit Dashboard (Figma 3649-6112 carrier slices).
     * Includes warm orange/yellow steps alongside teal, mint, and blue.
     */
    piePalette: [
      presets.turquoise['70'],
      presets.green['55'],
      presets.red['50'],
      presets.yellow['30'],
      presets.blue['80'],
      presets.blue['55'],
      presets.blue['50'],
      presets.blue['90'],
      presets.blue['10'],
      presets.turquoise['50'],
    ],
    /** Light-background donut — Port-to-Port carrier palette (Figma 3649-6112). */
    piePaletteLight: [...portToPortPiePalette],
    /** Port-to-Port Transit Dashboard chart series (Figma 3649-6112). */
    portToPort: portToPortChartSeries,
    /** Home dashboard snapshot charts (Figma Home — Dashboard, node 3711-4539). */
    homeDashboard: homeDashboardChartSeries,
  },
  drawerOverlay: {
    bg: '#00000033',
  },
  page: {
    dark: {
      gradientStart: '#0F3744',
      gradientEnd: '#010101',
    },
    light: {
      homeDashboard: homeDashboardAccents,
    },
  },
  /** Figma style tokens — values inherit from `presets.*`. */
  component: {
    metricCard: {
      dark: {
        labelColor: presets.neutral['0'],
        valueColor: presets.turquoise['30'],
        borderColor: presets.overlay.turquoise50,
        background: presets.overlay.white02,
        separatorColor: presets.overlay.turquoise50,
      },
      light: {
        labelColor: presets.neutral['90'],
        valueColor: presets.turquoise['70'],
        borderColor: presets.overlay.turquoise30,
        background: presets.overlay.black03,
        separatorColor: presets.overlay.turquoise30,
      },
    },
  },
  presets,
}
